import { useState } from 'react';
import RateReviewDialog, { IReviewSubmission } from './RateReviewDialog';
import RatingSummary, { IRatingStats } from './RatingSummary';

export interface IRatingReviewProps extends IRatingStats {
    /** Name of the thing being reviewed — a skill, connector, tool, or Cephable feature. */
    subject: string;
    /** The caller's own existing rating, pre-filled in the dialog. */
    myRating?: number;
    /** Slims the resting summary down to figures only. The dialog's strip always spells them out. */
    compact?: boolean;
    /** Show empty stars in the resting summary when nothing has been rated yet. */
    showEmptyRating?: boolean;
    /** Ratings needed before the average is shown at all. Defaults to 5. */
    minRatings?: number;
    /** When false the summary is inert — for viewers who can't rate (no seat, not a member). */
    canReview?: boolean;
    /** Drop the comment field when a surface only collects a score. */
    hideComment?: boolean;
    /** Repeat the aggregate figures inside the dialog. Defaults to true. */
    showStatsInDialog?: boolean;
    submitting?: boolean;
    /** Persist the review. Return a promise and the dialog stays open until it settles. */
    onSubmit: (submission: IReviewSubmission) => void | Promise<void>;
    className?: string;
}

/**
 * The drop-in pairing of {@link RatingSummary} and {@link RateReviewDialog}: the average, installs,
 * and comment counts at rest, which open the review dialog when activated. Use this in detail views
 * for skills, connectors, and Cephable features; reach for the two pieces separately only when a
 * surface needs to own the dialog state itself.
 *
 * Submission is the caller's: `onSubmit` persists against whichever API owns the thing being rated.
 * If it returns a promise the dialog shows a busy state and closes once it resolves, so a failed save
 * keeps the user's draft on screen.
 */
export default function RatingReview({
    subject,
    myRating,
    canReview = true,
    compact,
    showEmptyRating,
    hideComment,
    showStatsInDialog = true,
    submitting,
    onSubmit,
    className,
    minRatings,
    ...stats
}: Readonly<IRatingReviewProps>) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (submission: IReviewSubmission) => {
        try {
            setSaving(true);
            await onSubmit(submission);
            setOpen(false);
        } catch {
            // The caller owns error messaging (it knows which API failed); keep the dialog open so
            // the user's draft survives a failed save.
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <RatingSummary
                {...stats}
                compact={compact}
                showEmptyRating={showEmptyRating}
                minRatings={minRatings}
                className={className}
                onClick={canReview ? () => setOpen(true) : undefined}
            />
            {canReview && (
                <RateReviewDialog
                    open={open}
                    subject={subject}
                    stats={showStatsInDialog ? stats : undefined}
                    initialRating={myRating}
                    hideComment={hideComment}
                    submitting={submitting || saving}
                    onSubmit={handleSubmit}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
}
